import { COLORS, DSButton } from '@repo/design-system/index'
import {
  QIcon_check_filled,
  QIcon_close_filled,
  QIcon_effect,
  QIcon_exclamation_filled,
  QIcon_flag,
  QIcon_headphone,
  QIcon_mic_default,
  QIcon_mute,
  QIcon_pause_circle,
  QIcon_volumne,
} from '@repo/icons/index'
import { AnimatePresence, motion } from 'framer-motion'
import { useContext, useEffect, useRef, useState } from 'react'
import apis from '../../../modules/apis'
import { useConversationSetting } from '../../../modules/apis/student/conversation'
import { axiosPost } from '../../../modules/axiosHelper'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import { useIsOkToUse } from '../../../modules/hooks/useAppVersionStatus'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_ai_tutor, n_common } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import urls from '../../../modules/urls'
import { isB2BHRGroup, isMulticampus } from '../../../modules/utils'
import { AI_TUTOR_ACTIVITIES_MAP, useAITutorStack } from '../../../pages/[lang]/webview/ai-tutor'
import { SoundContext } from '../../common/audio/play/Audio'
import Record from '../../common/audio/record/Record'
import AITutorActivationDialog, { usePendingAITutorMembership } from './AITutorActivation'
import { AITutorPopupNoCreditB2B } from './AITutorPopup'
import { PronunciationFeedbackSheet } from './PronunciationFeedback'
import RecordWrapper from './activity/RecordWrapper'

export type Word = {
  keyword: boolean
  word: string
  correct?: boolean | null
  separator: boolean
  hidden?: boolean
  empty?: boolean
  idx?: number

  ps?: boolean
  ns?: boolean
}

export const TextBoxWord = ({
  word,
  correct = null,
  keyword = false,
  blind = false,
  fixed = false,
  ns = false,
  highlight = false,
  highlightExt = false,
  hide = false,
  onClick = null,
  background = 'transparent',
  isKeyboardMode = false,
  onChangeValue = null,
  typedValue = '',
  inputRef = null,
  showAll = false,
}) => {
  let color = 'text-gray-600'
  if (hide) color = 'text-transparent'
  else if (correct === true) color = 'text-purple-500'
  else if (correct === false) color = 'text-red-500'

  const [isFocused, setIsFocused] = useState(false)
  const spanRef = useRef(null)
  const [inputWidth, setInputWidth] = useState(0)

  const onChangeTyping = (w) => {
    onChangeValue(w.toLowerCase())
  }

  useEffect(() => {
    if (spanRef.current) {
      setInputWidth(spanRef.current.offsetWidth)
    }
  }, [word])

  return (
    <span
      className={`${color} relative inline-flex items-center
      ${blind && 'bg-white'}
      ${keyword ? 'border border-gray-200 px-[8px] py-[2px] mb-[4px] ml-[4px] rounded-lg' : ''} ${
        isFocused && isKeyboardMode ? 'border-gray-600' : ''
      } ${ns ? 'mr-[4px]' : ''} ${highlight ? 'bg-purple-100' : ''}`}
      onClick={onClick}
    >
      <span
        ref={spanRef}
        // pointer-events-none 없으면 input이 overwite되어서 안눌림
        className={`absolute opacity-0 whitespace-pre Title1-20Bd ${isKeyboardMode && 'pointer-events-none'}`}
      >
        {word ? word : 'Fixed'}
      </span>
      <input
        type="text"
        ref={inputRef}
        value={blind && isKeyboardMode ? typedValue : word}
        onChange={(e) => onChangeTyping(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={!blind || !isKeyboardMode}
        style={{
          all: 'unset',
          border: 'none',
          outline: 'none',
          width: inputWidth ? `${inputWidth + 4}px` : 'auto',
          background: blind && !isKeyboardMode ? 'white' : 'none',
          marginTop: 1,
          // opacity: blind && !isKeyboardMode ? 0 : 1,

          opacity: showAll ? 1 : blind && !isKeyboardMode ? 0 : 1, // <--- MODIFIED LINE

          whiteSpace: 'nowrap',
          color: highlightExt ? '#442dc9' : null,
        }}
      />
    </span>
  )
}

export const Nudge = ({
  children = null,
  title = null,
  className = 'Body2-14Md',
  titleClassName = 'Body2-14Md text-gray-500',
  middle = false,
  color = COLORS.PURPLE400,
}) => (
  <div
    className={`bg-gray-100 rounded-[8px] p-[12px] flex flex-col gap-[8px] justify-start w-full ${
      middle ? 'items-center' : 'items-start'
    }`}
  >
    <div className="flex flex-row gap-[8px] items-center justify-start">
      <QIcon_effect color={color} width={20} height={20} />
      <span className={titleClassName}>{title}</span>
    </div>
    {children && (
      <div className="flex flex-col items-start justify-start flex-1">
        <div className={`text-gray-700 text-left self-stretch ${className}`}>{children}</div>
      </div>
    )}
  </div>
)

export const Box = ({
  children,
  big = false,
  active = false,
  onClick = null,
  correct = null,
  root = null,
  bold = false,
  white = false,
}) => (
  <div
    className={`flex flex-col items-start justify-start self-stretch relative w-full ${
      correct === true || active ? 'text-purple-500' : ''
    } ${correct === false ? 'text-red-500' : ''} ${active === false ? 'text-gray-700' : ''}`}
    onClick={onClick}
  >
    {root}
    <div
      className={`rounded-[16px] border-[1.5px] flex flex-col items-center justify-center self-stretch ${
        white ? 'bg-white' : 'bg-gray-50'
      } ${big ? 'min-h-[200px] px-[24px] py-[32px]' : 'px-[20px] py-[16px]'} relative ${
        active
          ? correct === true || correct === null
            ? 'border-purple-500 bg-purple-50'
            : correct === false
            ? 'border-red-500 bg-red-100'
            : 'border-gray-200'
          : 'border-gray-200'
      }`}
    >
      <div className={`${big ? 'Title1-20Bd' : bold ? 'Body1-16Bd' : 'Body1-16Md'} relative self-stretch`}>
        {children}
      </div>
    </div>
  </div>
)

export const TextBox = ({
  words,
  blind = false as boolean | number,
  fixed = false,
  highlight = false,
  highlightExt = false,
  onClick = null,
  onClickBox = null,
  feedback = false,
  textKo = '',
  showKo = false,
  isKeyboardMode = false,
  typedAnswers = null,
  setTypedAnswers = null,
  showAll = false,
}: {
  words: Word[]
  blind?: boolean | number
  fixed?: boolean
  highlight?: boolean
  highlightExt?: boolean
  showAll?: boolean
  onClick?: (idx: number) => void
  onClickBox?: () => void
  feedback?: boolean
  textKo?: string
  showKo?: boolean
  isKeyboardMode?: boolean
  typedAnswers?: Record<number, string>
  setTypedAnswers?: any
}) => {
  const { t } = useTranslation()

  const onChangeValue = (index: number, val: string) => {
    setTypedAnswers((prev) => ({ ...prev, [index]: val }))
  }

  const inputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({})

  useEffect(() => {
    if (isKeyboardMode) {
      const firstHiddenIndex = words.findIndex((w) => w.hidden)
      if (firstHiddenIndex >= 0) {
        inputRefs.current[firstHiddenIndex]?.focus()
      }
    }
  }, [isKeyboardMode, words])

  return (
    <Box
      big
      onClick={onClickBox}
      root={
        feedback && (
          <div className="absolute top-[6px] left-[6px] text-gray-700 text-left p-[6px] bg-gray-100 rounded-lg Caption-12Bd">
            {t(n_ai_tutor.study_activity.detail5)}
          </div>
        )
      }
    >
      {words.map((word, i) => {
        const blindIt = (blind === true && (!word.word || word.hidden)) || (blind !== true && (blind as any) <= i)
        return (
          <TextBoxWord
            {...word}
            key={i}
            blind={blindIt && word.keyword}
            fixed={fixed && blindIt && word.keyword}
            highlight={highlight && blind === i}
            highlightExt={highlightExt}
            onClick={() => onClick?.(i)}
            background="bg-white"
            showAll={showAll}
            isKeyboardMode={isKeyboardMode}
            onChangeValue={(val: string) => onChangeValue(i, val)}
            typedValue={typedAnswers?.[i] || ''}
            inputRef={(el: HTMLInputElement) => (inputRefs.current[i] = el)}
          />
        )
      })}

      {showKo && <div className="Body1-16Md text-left w-full text-gray-500 mt-[12px]">{textKo}</div>}
    </Box>
  )
}

export const CircularProgress = ({ progress, size, width = 4, rail = false }) => (
  <svg className="progress-circle -rotate-90 absolute" width={size} height={size} viewBox="0 0 120 120">
    {rail && (
      <circle
        className="fill-transparent stroke-gray-200"
        cx="60"
        cy="60"
        r="54"
        strokeLinecap="round"
        strokeWidth={width}
      />
    )}
    <circle
      className={`fill-transparent ${progress === 100 ? 'stroke-green-100' : 'stroke-purple-500'}`}
      cx="60"
      cy="60"
      r="54"
      strokeLinecap="round"
      strokeWidth={width}
      strokeDasharray="339.292"
      strokeDashoffset={339.292 * (1 - progress / 100)}
    />
  </svg>
)

export const LinearProgress = ({ progress, height = 4 }) => {
  return (
    <div className="w-full bg-gray-200 rounded-full relative" style={{ height }}>
      <div
        className="h-full bg-purple-500 rounded-full absolute left-0 top-0"
        style={{ width: `${Math.min(progress, 100)}%` }}
      />
    </div>
  )
}

export const Solved = ({ solved, onNext = null, onRetry = null, explanation = '', idx }) => {
  const { t } = useTranslation()
  const { useAITutorFlow } = useAITutorStack()
  const { push } = useAITutorFlow()
  const router = useAppRouter()
  const configs = {
    done: {
      bgColor: 'bg-purple-50',
      icon: <QIcon_check_filled className="w-[26px] h-[26px]" color={COLORS.PURPLE500} />,
      titleColor: 'text-purple-500',
      title: t(n_ai_tutor.study_activity.fb_title1),
      subtitle: t(n_ai_tutor.study_activity.fb_body1),
      buttonColor: 'bg-purple-500',
      iconColor: COLORS.PURPLE500,
      buttonText: t(n_ai_tutor.study_activity.continue_btn),
      onClick: onNext,
    },
    'retry-audio': {
      bgColor: 'bg-yellow-200',
      icon: <QIcon_exclamation_filled className="w-[26px] h-[26px]" color={COLORS.YELLOW500} />,
      titleColor: 'text-yellow-500',
      title: t(n_ai_tutor.study_activity.fb_title2),
      subtitle: t(n_ai_tutor.study_activity.fb_body2),
      iconColor: COLORS.YELLOW500,
      buttonColor: 'bg-yellow-500',
      buttonText: t(n_ai_tutor.study_activity.again_btn),
      onClick: onRetry,
      showNextButton: true,
    },
    'fail-audio': {
      bgColor: 'bg-yellow-100',
      icon: <QIcon_exclamation_filled className="w-[26px] h-[26px]" color={COLORS.YELLOW500} />,
      titleColor: 'text-yellow-500',
      title: t(n_ai_tutor.study_activity.fb_title2),
      subtitle: t(n_ai_tutor.study_activity.fb_body3),
      iconColor: COLORS.YELLOW500,
      buttonColor: 'bg-yellow-500',
      buttonText: t(n_ai_tutor.study_activity.continue_btn),
      onClick: onNext,
    },
    fail: {
      bgColor: 'bg-red-100',
      icon: <QIcon_close_filled className="w-[26px] h-[26px]" color={COLORS.RED400} />,
      titleColor: 'text-red-400',
      title: t(n_ai_tutor.study_activity.fb_title3),
      subtitle: t(n_ai_tutor.study_activity.fb_body5),
      iconColor: COLORS.RED400,
      buttonColor: 'bg-red-400',
      buttonText: t(n_ai_tutor.study_activity.continue_btn),
      onClick: onNext,
    },
  }

  const lessonId = router.query?.lessonId
  const config = configs[solved]
  if (!config) return null

  const handleClickReport = () => {
    push(AI_TUTOR_ACTIVITIES_MAP.AITutorReportActivity, { activityId: idx, lessonId: lessonId })
  }

  return (
    <motion.div
      initial={{ y: '200px' }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', damping: 20, stiffness: 300 }}
      className={`${config.bgColor} rounded-tl-xl rounded-tr-xl pt-[16px] pr-[16px] pb-[31px] pl-[16px] flex flex-col gap-[16px] items-center justify-start self-stretch shrink-0 overflow-hidden bottom-0 left-0 right-0 z-10 absolute w-screen`}
    >
      <div className="flex flex-col gap-[8px] items-start justify-start self-stretch shrink-0 relative">
        <div className="flex flex-row gap-[8px] items-center justify-start self-stretch shrink-0 relative">
          {config.icon}
          <div className={`${config.titleColor} text-left Title1-20Bd relative`}>{config.title}</div>
          <div className="ml-auto" />
          <QIcon_flag width={20} height={20} onClick={handleClickReport} color={config.iconColor} />
        </div>
        <div className="text-gray-600 text-left Body1-16Md relative self-stretch">{explanation || config.subtitle}</div>
      </div>
      <div className="flex items-center justify-center self-stretch shrink-0 relative w-full gap-[8px]">
        {config.showNextButton && (
          <div
            className="bg-white rounded-xl border-solid border-gray-300 border-[1.5px] px-[28px] py-[12px] flex items-center justify-center shrink-0 w-[100px] relative"
            onClick={onNext}
          >
            <div className="text-gray-700 text-center Body2-14Bd relative">{t(n_common.btn_next)}</div>
          </div>
        )}
        <div
          className={`${config.buttonColor} rounded-xl px-[28px] py-[12px] flex items-center justify-center flex-1 relative`}
          onClick={config.onClick}
        >
          <div className="text-white text-left Body2-14Bd relative">{config.buttonText}</div>
        </div>
      </div>
    </motion.div>
  )
}

export const ActivityScreen = ({
  children,
  title,
  solved,
  onRetry = null,
  nextActivity = null,
  explanation = '' as any,
  retry = false,
  key = null,
  idx = null,
}) => {
  const { t } = useTranslation()

  return (
    <div key={key} className="h-full" style={{}}>
      <div className="flex flex-col gap-[32px] items-start justify-start self-stretch flex-1 relative px-[20px] py-[20px] h-full">
        {retry && (
          <div className="bg-purple-50 rounded px-[6px] py-[2px] flex flex-row gap-1 items-start justify-start shrink-0 relative">
            <div className="text-purple-500 text-left Body2-14Bd relative">{t(n_ai_tutor.study_activity.label4)}</div>
          </div>
        )}
        <div className="flex flex-col items-start justify-start relative">
          <div className="flex flex-col gap-[4px] items-start justify-start self-stretch shrink-0 relative">
            <div className="text-left Title1-20Bd relative">{title}</div>
          </div>
        </div>
        {children}
      </div>
      <Solved solved={solved} onNext={nextActivity} onRetry={onRetry} explanation={explanation} idx={idx} />
    </div>
  )
}

export const TextRecordActivity = ({
  title,
  words,
  audio,
  handleStopRecording = null,
  handleSubmitText = null,
  blind = false as boolean | number,
  fixed = false,
  highlight = true,
  nextActivity = null,
  solved = null,
  setSolved = null,
  retryCnt = 0,
  retry = false,
  audio_url = '',
  autoStart = true,
  textKo = '',
  showKo = false,
  idx = null,
  wordCount = 0,
}: {
  title: string
  subtitle?: string
  words: Word[]
  blind?: boolean | number
  fixed?: boolean
  highlight?: boolean
  handleStopRecording?: (text: string, audio: ArrayBuffer) => void
  handleSubmitText?: (text: string) => void
  audio?: ArrayBuffer
  nextActivity?: () => void
  solved?: null | 'done' | 'retry-audio' | 'fail' | 'fail-audio'
  setSolved?: (solved: null | 'done' | 'retry-audio' | 'fail' | 'fail-audio') => void
  retryCnt?: number
  retry?: boolean
  audio_url?: string
  autoStart?: boolean
  textKo?: string
  showKo?: boolean
  idx?: any
  wordCount?: number
}) => {
  const { t } = useTranslation()
  const sound = useContext(SoundContext)
  const [feedback, setFeedback] = useState(null)
  const [playingRecord, setPlayingRecord] = useState(false)
  const [playingAudio, setPlayingAudio] = useState(false)
  const [isRecording, setIsRecording] = useState<boolean>(false)
  const [skipped, setSkipped] = useState(false)
  const [isKeyboardMode, setIsKeyboardMode] = useState(false)
  const [typedAnswers, setTypedAnswers] = useState<Record<number, string>>({})
  const [waitingTranscription, setWaitingTranscription] = useState(false)
  const setting = useConversationSetting()
  const { currentUser } = useCurrentUser()

  const [highlightText, setHighlightText] = useState(false)
  // const isWhisperSTTOn = useFeatureFlag(HACKLE_FLAG_KEY.AI_TUTOR_STT_NEXT)
  // const { data: customerType } = useCustomerType()

  const [recordingStatus, setRecordingStatus] = useState<any>('idle')
  const [recordPayload, setRecordPayload] = useState<any>(null)

  const isOkToUse = useIsOkToUse()

  useEffect(() => {
    sound.speed = setting.data?.speed || 1
  }, [setting])

  const playRecord = () => {
    if (isRecording) return
    if (!audio) return
    setPlayingRecord(true)
    sound.stop()
    sound.play([new Blob([audio])]).then(() => setPlayingRecord(false))
  }

  function handleAudioEnded() {
    setPlayingAudio(false)
  }

  const playAudio = () => {
    if (isRecording) return
    if (!audio_url) return
    setPlayingAudio(true)

    if (!isOkToUse) {
      sound.stop()
      sound.play([audio_url]).then(() => setPlayingAudio(false))
    } else {
      // alert('let it play')
      // alert('let it play')
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'playAudio', url: audio_url }))
    }
  }

  const stopAudio = () => {
    setPlayingRecord(false)
    setPlayingAudio(false)

    if (!isOkToUse) {
      sound.stop()
    } else {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'stopAudio' }))
    }
  }

  function generateRandomId(length = 8): string {
    // toString(36) turns numbers into 0–9a–z. slice off the "0." prefix.
    return Math.random()
      .toString(36)
      .substring(2, 2 + length)
  }

  useEffect(() => {
    function handleMessage(event) {
      let data
      try {
        data = JSON.parse(event.data)
      } catch (e) {
        console.error('Invalid message format:', e)
        return
      }

      if (data.type === 'recording') {
        setRecordingStatus('recording')
      }

      if (data.type === 'audioEnded') {
        handleAudioEnded()
        // handleAudioEnded(data.url)
      }

      if (data.type === 'canceled') {
        setRecordingStatus('idle')
      }

      //recordingResult는 RN에 남음. 다만..
      if (data.type === 'recordingResult') {
        setRecordPayload({
          id: generateRandomId(), // e.g. "xk3j5b9q"
          transcription: data.transcription,
          audioBlob: null, // RN 쪽에서 blob 처리한다면 RN 메시지로 받을 수도 있고,
        })

        setRecordingStatus('idle')
      }
    }

    window.addEventListener('message', handleMessage)
    document.addEventListener('message', handleMessage) // iOS 대응

    // 클린업
    return () => {
      window.removeEventListener('message', handleMessage)
      document.removeEventListener('message', handleMessage)
    }
  }, [])

  useEffect(() => {
    if (!recordPayload) return

    // payload가 들어올 때마다 이펙트에서 처리
    handleStopRecording(recordPayload.transcription, recordPayload.audioBlob)

    // (선택) 반복 처리 방지를 위해 페이로드 초기화
    setRecordPayload(null)
  }, [recordPayload])

  useEffect(() => {
    if (autoStart) playAudio()
  }, [autoStart])

  const onClickWord = () => {
    if (!audio) return
    if (!currentUser?.is_ringle_member) return

    setFeedback(true)
  }

  const onClickKeyboardMode = () => {
    const hasHiddenWords = words.some((word) => word.hidden)

    if (hasHiddenWords) {
      setSkipped(true)
      setHighlightText(true)
      playAudio()
      setSolved?.('done')
    } else {
      setSkipped(true)

      setHighlightText(true)
      playAudio()
      setSolved?.('done')
      setIsKeyboardMode(false)
    }
  }

  const onClickSubmit = () => {
    // alert("s')")

    let sentence: string = ''

    words.map((word: Word, index: number) => {
      if (word.hidden) {
        sentence += typedAnswers[index] + ' '
      } else {
        sentence += word.word + ' '
      }
    })
    setIsKeyboardMode(false)
    handleSubmitText(sentence?.toLowerCase())
  }

  const onCancel = () => {
    try {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'cancelRecording' }))
    } catch (e) {
      console.log(e)
    }
    setRecordingStatus('cancelling')
  }

  console.log('🟢 words ====>', words)

  return (
    <ActivityScreen
      title={title}
      solved={solved}
      onRetry={() => setSolved?.(null)}
      nextActivity={nextActivity}
      retry={retry}
      idx={idx}
      explanation={skipped && t(n_ai_tutor.study_activity.fb_skip)}
    >
      <TextBox
        words={words}
        blind={blind}
        fixed={fixed}
        highlight={highlight}
        highlightExt={highlightText}
        onClickBox={() => onClickWord()}
        feedback={!!audio}
        showAll={highlightText}
        textKo={textKo}
        showKo={showKo}
        isKeyboardMode={isKeyboardMode}
        typedAnswers={typedAnswers}
        setTypedAnswers={setTypedAnswers}
      />
      <div className="flex gap-[16px] items-start justify-between self-stretch shrink-0 relative mt-[-16px]">
        {playingAudio ? (
          <div
            className="bg-white rounded-[100px] border-gray-200 border-[1.5px] px-[16px] py-[12px] flex flex-row gap-[8px] items-center justify-start relative"
            onClick={stopAudio}
          >
            <div className="flex flex-row gap-[6px] items-start justify-start relative">
              <QIcon_pause_circle color={COLORS.GRAY500} width={20} height={20} />
            </div>
            <div className="text-gray-700 text-left Body2-14Bd relative">{t(n_ai_tutor.study_activity.listen_btn)}</div>
          </div>
        ) : (
          <div
            className="bg-white rounded-[100px] border-gray-200 border-[1.5px] px-[16px] py-[12px] flex flex-row gap-[8px] items-center justify-start relative"
            onClick={playAudio}
          >
            <div className="flex flex-row gap-[6px] items-start justify-start relative">
              <QIcon_volumne color={COLORS.GRAY500} width={20} height={20} />
            </div>
            <div className="text-gray-700 text-left Body2-14Bd relative">{t(n_ai_tutor.study_activity.listen_btn)}</div>
          </div>
        )}

        {isOkToUse && (
          <div
            className="bg-white rounded-[100px] border-gray-200 border-[1.5px] px-[16px] py-[12px] flex flex-row gap-[8px] items-center justify-start relative"
            onClick={() => {
              window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'playLastRecording' }))
            }}
          >
            <div className="flex flex-row gap-[6px] items-start justify-start relative"></div>
            <div className="text-gray-700 text-left Body2-14Bd relative">{t(n_ai_tutor.study_activity.btn3)}</div>
          </div>
        )}

        {audio &&
          (playingRecord ? (
            <div
              className="bg-white rounded-[100px] border-gray-200 border-[1.5px] px-[16px] py-[12px] flex flex-row gap-[8px] items-center justify-start relative"
              onClick={stopAudio}
            >
              <div className="flex flex-row gap-[6px] items-start justify-start relative">
                <QIcon_pause_circle color={COLORS.GRAY500} width={20} height={20} />
              </div>
              <div className="text-gray-700 text-left Body2-14Bd relative">{t(n_ai_tutor.study_activity.btn3)}</div>
            </div>
          ) : (
            <div
              className="bg-white rounded-[100px] border-gray-200 border-[1.5px] px-[16px] py-[12px] flex flex-row gap-[8px] items-center justify-start relative"
              onClick={playRecord}
            >
              <div className="flex flex-row gap-[6px] items-start justify-start relative">
                <QIcon_headphone color={COLORS.GRAY500} width={20} height={20} />
              </div>
              <div className="text-gray-700 text-left Body2-14Bd relative">{t(n_ai_tutor.study_activity.btn3)}</div>
            </div>
          ))}
      </div>

      {solved === null && !isKeyboardMode && (
        <>
          {isOkToUse ? (
            <div
              className={'opacity-95 flex flex-col bottom-[0px] items-center justify-center pt-[12px] mt-[auto]'}
              style={{
                width: '100%',
                paddingBottom: `calc(12px + env(safe-area-inset-bottom))`,
                height: 148,
                position: 'absolute',
                left: 0,
              }}
            >
              <RecordWrapper
                recordingStatus={recordingStatus}
                setRecordingStatus={setRecordingStatus}
                onCancel={onCancel}
              />
            </div>
          ) : (
            <Record
              handleStopRecording={handleStopRecording}
              autoStart={retryCnt > 0}
              helper={t(n_ai_tutor.study_activity.detail1)}
              onStartRecord={() => {
                setIsRecording(true)
              }}
              onFinishRecord={() => {
                setIsRecording(false)
              }}
              beforeRecord={() => {
                sound.stop()
              }}
              noMargin
              wordCount={wordCount}
            />
          )}

          {!isRecording && recordingStatus !== 'recording' && !waitingTranscription && (
            <KeyboardModeBtn onClickKeyboardMode={onClickKeyboardMode} />
          )}
        </>
      )}
      <PronunciationFeedbackSheet show={feedback} setShow={setFeedback} audio={audio} words={words} />
      {isKeyboardMode && <KeyboardToolbar setIsKeyboardMode={setIsKeyboardMode} onClickSubmit={onClickSubmit} />}
    </ActivityScreen>
  )
}

export const KeyboardToolbar = ({ setIsKeyboardMode, onClickSubmit }) => {
  const [bottomOffset, setBottomOffset] = useState(0)
  const { t } = useTranslation()

  useEffect(() => {
    // 일부 브라우저(특히 데스크톱)에서는 visualViewport가 없을 수 있으므로 방어코드

    if (!window.visualViewport) return

    const handleResize = () => {
      const visualViewport = window.visualViewport
      // 화면에서 키보드가 차지하는 영역이 있으면 그만큼 위로 띄워야 한다.
      // = 뷰포트 전체 높이 - 실제 보이는 높이 - (뷰포트의 offsetTop)
      const newBottomOffset = window.innerHeight - visualViewport.height - visualViewport.offsetTop

      // 키보드가 없는 경우 newBottomOffset은 0 혹은 음수가 될 수 있다.
      setBottomOffset(Math.max(newBottomOffset, 0))
    }

    // 초기 실행 및 resize 시 이벤트 등록
    handleResize()
    window.visualViewport.addEventListener('resize', handleResize)
    window.visualViewport.addEventListener('scroll', handleResize)
    return () => {
      window.visualViewport.removeEventListener('resize', handleResize)
      window.visualViewport.removeEventListener('scroll', handleResize)
    }
  }, [])

  if (!window.visualViewport) return

  return (
    <div
      className="fixed left-0 w-full flex flex-row shadow-[0_-2px_20px_0_rgba(0,0,0,0.1)] bg-white px-[16px] py-[8px]"
      style={{
        bottom: bottomOffset, // 키보드 높이만큼 띄움
        paddingBottom: bottomOffset == 0 && '52px',
        // transition: 'bottom 0.2s', // 자연스럽게 애니메이션
      }}
    >
      <div className="w-full justify-between flex flex-row">
        <div className="flex flex-col items-center justify-center" onClick={() => setIsKeyboardMode(false)}>
          <QIcon_mic_default width={24} height={24} color={COLORS.GRAY400} />
          <div className="text-gray-400 Body2-14Md">{t(n_ai_tutor.study_speaking.bottom_mode_2)}</div>
        </div>

        <div>
          <DSButton onClick={() => onClickSubmit()}>
            <DSButton.Label>{t(n_ai_tutor.error_report.submit)}</DSButton.Label>
          </DSButton>
        </div>
      </div>
    </div>
  )
}

export const KeyboardModeBtn = ({ onClickKeyboardMode }) => {
  const { t } = useTranslation()
  return (
    <div className="absolute right-[34px] bottom-[38px]" onClick={onClickKeyboardMode}>
      <div className="flex flex-col justify-center items-center">
        <QIcon_mute width={24} height={24} />
        <div className="Body2-14Md text-gray-400">{t(n_ai_tutor.study_speaking.bottom_mode)}</div>
      </div>
    </div>
  )
}

const transition = { type: 'spring', stiffness: 600, bounce: 0.15, damping: 80, restDelta: 0.001 }
const SCALE = 1
const variants = {
  before: (t) => ({ x: t ? '100%' : '-100%', transition, scale: SCALE }),
  show: { x: '0%', transition, scale: 1 },
  after: (t) => ({ x: t ? '-100%' : '100%', opacity: 0, transition, scale: SCALE }),
}

export const HorizontalStack = ({ menu = [], current = 0 }) => {
  const [prev, setPrev] = useState(current)
  const [curr, setCurr] = useState(current)
  const ref = useRef(null)

  useEffect(() => {
    setPrev(curr)
    setCurr(current)
  }, [current])

  useEffect(() => {
    if (ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [current, ref])

  return (
    <div className="grid grid-cols-1 w-full overflow-hidden">
      {menu.map((e, i) => {
        if (i !== curr) return null

        return (
          <AnimatePresence key={i} initial={false} custom={prev < curr}>
            <div className="col-start-1 row-start-1" ref={ref}></div>
            {i === curr && (
              <motion.div
                variants={variants}
                initial="before"
                animate="show"
                exit="after"
                custom={prev < curr}
                className="col-start-1 row-start-1"
                key={i}
              >
                {e()}
              </motion.div>
            )}
          </AnimatePresence>
        )
      })}
    </div>
  )

  return (
    <div className="grid grid-cols-1 w-full overflow-hidden">
      {menu.map((e, i) => (
        <AnimatePresence key={i} initial={false} custom={prev < curr}>
          <div className="col-start-1 row-start-1" ref={ref}></div>
          {i === curr && (
            <motion.div
              variants={variants}
              initial="before"
              animate="show"
              exit="after"
              custom={prev < curr}
              className="col-start-1 row-start-1"
              key={i}
            >
              {e()}
            </motion.div>
          )}
        </AnimatePresence>
      ))}
    </div>
  )
}

export const purifyText = (text: string | Word) => {
  if (typeof text === 'string') return text.replace(/[^\w\s]/gi, '').toLowerCase()
  return ''
}

export const AITutorEnterLesson = ({ comp, lessonId, tabId = 0, replace = false, fail = false }) => {
  const { currentUser } = useCurrentUser()
  const isB2B = isMulticampus(currentUser) || isB2BHRGroup(currentUser)
  const [b2bModalIsOpen, setB2BModalIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const router = useAppRouter()
  const getFullUrl = useReturnFullUrl()
  const isWebview = useIsWebview()

  const [showMembershipActivation, setShowMembershipActivation] = useState(false)
  const { data: pendingMembership } = usePendingAITutorMembership()
  const hasPendingMembership = pendingMembership?.user_ai_tutor_permissions?.length > 0

  const checkLessonAvailability = async () => {
    const response = await axiosPost(apis.aiTutor.lesson.available({ lessonId }))
    return response
  }

  const showMembershipActivationDialog = () => {
    setShowMembershipActivation(true)
    if (isWebview) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'HIDE_TAB_BAR' }))
    }
  }

  const hideMembershipActivationDialog = () => {
    setShowMembershipActivation(false)
    if (isWebview) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'SHOW_TAB_BAR' }))
    }
  }

  const enterLesson = async () => {
    setLoading(true)

    // alert(replace)

    const res = fail ? { success: false } : await checkLessonAvailability()
    if (res.success !== false) {
      const url = urls.webview.aiTutor.activity(lessonId, tabId)

      // alert(replace)

      if (!replace) {
        router
          .push(url, {
            appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
            appSendData: { url: getFullUrl(url) },
          })
          .then(() => setLoading(false))
      } else {
        router
          .replace(url, {
            appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
            appSendData: { url: getFullUrl(url) },
          })
          .then(() => {
            setLoading(false)
          })
      }
    } else {
      if (isB2B) {
        setB2BModalIsOpen(true)
        setLoading(false)
      } else if (hasPendingMembership) {
        setLoading(false)
        showMembershipActivationDialog()
        return
      } else {
        const url = urls.webview.aiTutor.purchase
        router
          .push(url, {
            appScreenName: APP_SCREEN_NAME.IAP_SCREEN,
            appSendData: { url: getFullUrl(url) },
          })
          .then(() => setLoading(false))
      }
    }
  }

  return (
    <>
      {comp(enterLesson, loading)}
      <AITutorPopupNoCreditB2B show={b2bModalIsOpen} setShow={setB2BModalIsOpen} />
      <AITutorActivationDialog
        isOpen={showMembershipActivation}
        onClose={hideMembershipActivationDialog}
        onSuccess={enterLesson}
      />
    </>
  )
}

export function judgeAnswer(answer: Word[], response: string) {
  const recWords = new Set(
    response
      .replaceAll('"', ` " `)
      .replaceAll("'", ` ' `)
      .replaceAll(',', ' , ')
      .replaceAll('.', ' . ')
      .replaceAll('  ', ' ')
      .split(' ')
      .map((e) => purifyText(e))
      .filter((e) => e)
  )
  let correctCnt = 0
  let totalCnt = 0
  const result = []
  for (const token of answer) {
    const pr = purifyText(token.word)
    if (!token.separator) {
      result.push({ ...token, correct: recWords.has(pr), hidden: false })
      if (recWords.has(pr)) correctCnt++
      totalCnt++
    } else result.push({ ...token, correct: null, hidden: false })
  }
  return { result, correctCnt, totalCnt }
}
